/**
 * Tenant Context - Life-and-Death Grade Tenant Isolation
 *
 * This system ensures complete tenant isolation throughout the application,
 * preventing cross-tenant data access that could be catastrophic in
 * healthcare/financial scenarios.
 */
import { z } from 'zod';
import sha256 from 'crypto-js/sha256';
/**
 * Tenant information schema with validation
 */
const TenantInfoSchema = z.object({
    id: z.string().min(1, 'Tenant ID cannot be empty'),
    name: z.string().min(1, 'Tenant name cannot be empty'),
    type: z.enum(['healthcare', 'financial', 'manufacturing', 'government', 'enterprise']),
    tier: z.enum(['basic', 'professional', 'enterprise', 'critical']),
    region: z.string().optional(),
    features: z.array(z.string()).default([]),
    metadata: z.record(z.unknown()).optional()
});
/**
 * User context within tenant scope
 */
const UserContextSchema = z.object({
    id: z.string().min(1),
    email: z.string().email().optional(),
    roles: z.array(z.string()).min(1, 'User must have at least one role'),
    permissions: z.array(z.string()).default([]),
    actorId: z.string().optional(),
    sessionId: z.string().optional(),
    metadata: z.record(z.unknown()).optional()
});
/**
 * Request context for tracing and auditing
 */
const RequestContextSchema = z.object({
    correlationId: z.string().min(1), // Support both legacy and Snowflake ID formats
    requestId: z.string().min(1), // Support both legacy and Snowflake ID formats
    timestamp: z.date(),
    source: z.enum(['web', 'mobile', 'api', 'system', 'integration']),
    userAgent: z.string().optional(),
    ipAddress: z.string().optional(),
    metadata: z.record(z.unknown()).optional()
});
/**
 * Production-grade tenant context implementation
 */
export class TenantContext {
    constructor(tenant, user = null, request) {
        this._accessCache = new Map();
        this._cacheTimeout = 5 * 60 * 1000; // 5 minutes
        // Validate inputs with Zod schemas
        this.tenant = TenantInfoSchema.parse(tenant);
        this.user = user ? UserContextSchema.parse(user) : null;
        this.request = RequestContextSchema.parse({
            correlationId: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            requestId: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            timestamp: new Date(),
            source: 'web',
            ...request
        });
        // Create tenant hash for security validations
        this._tenantHash = this.createTenantHash();
    }
    /**
     * Create secure hash of tenant for validation
     */
    createTenantHash() {
        const data = `${this.tenant.id}:${this.tenant.type}:${this.tenant.tier}`;
        return sha256(data).toString();
    }
    /**
     * Validate access to resource with caching
     */
    async validateAccess(resource, operation) {
        // Check cache first
        const cacheKey = `${resource}:${operation}:${this.user?.id || 'anonymous'}`;
        const cached = this._accessCache.get(cacheKey);
        if (cached && Date.now() < cached.expires) {
            return cached.result;
        }
        try {
            const result = await this.performAccessValidation(resource, operation);
            // Cache the result
            this._accessCache.set(cacheKey, {
                result,
                expires: Date.now() + this._cacheTimeout
            });
            return result;
        }
        catch (error) {
            // On validation failure, deny access
            console.error('Access validation failed', {
                tenant: this.tenant.id,
                resource,
                operation,
                error
            });
            return false;
        }
    }
    /**
     * Perform actual access validation (override in subclasses)
     */
    async performAccessValidation(resource, operation) {
        if (!this.user) {
            return false; // No user context = no access
        }
        // Basic permission checking
        const requiredPermission = `${resource}:${operation}`;
        if (this.user.permissions.includes(requiredPermission)) {
            return true;
        }
        // Role-based access checking
        const adminRoles = ['admin', 'super_admin', 'tenant_admin'];
        if (adminRoles.some(role => this.user?.roles.includes(role))) {
            return true;
        }
        // Resource-specific role checking
        const resourceRole = `${resource}_${operation}`;
        if (this.user?.roles.includes(resourceRole)) {
            return true;
        }
        return false;
    }
    /**
     * Check if user has specific permission
     */
    hasPermission(permission) {
        return this.user?.permissions.includes(permission) || false;
    }
    /**
     * Check if user has specific role
     */
    hasRole(role) {
        return this.user?.roles.includes(role) || false;
    }
    /**
     * Check if feature is enabled for this tenant
     */
    isFeatureEnabled(feature) {
        return this.tenant.features.includes(feature);
    }
    /**
     * Create child context for nested operations
     */
    createChildContext(overrides) {
        const childRequest = {
            ...this.request,
            requestId: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`, // New request ID
            timestamp: new Date(),
            ...overrides
        };
        return new TenantContext(this.tenant, this.user, childRequest);
    }
    /**
     * Get current tenant ID
     */
    getCurrentTenant() {
        return this.tenant.id;
    }
    /**
     * Get current user ID
     */
    getCurrentUser() {
        return this.user?.id || null;
    }
    /**
     * Get correlation ID for request tracing
     */
    getCorrelationId() {
        return this.request.correlationId;
    }
    /**
     * Create audit context for compliance logging
     */
    toAuditContext() {
        return {
            tenantId: this.tenant.id,
            userId: this.user?.id || null,
            correlationId: this.request.correlationId,
            requestId: this.request.requestId,
            timestamp: this.request.timestamp.toISOString(),
            source: this.request.source,
            actorId: this.user?.actorId,
            sessionId: this.user?.sessionId,
            metadata: {
                tenantType: this.tenant.type,
                tenantTier: this.tenant.tier,
                userRoles: this.user?.roles,
                ...this.request.metadata
            }
        };
    }
    /**
     * Create log context for structured logging
     */
    toLogContext() {
        return {
            tenant: this.tenant.id,
            user: this.user?.id || null,
            correlationId: this.request.correlationId,
            requestId: this.request.requestId,
            timestamp: this.request.timestamp.toISOString(),
            component: 'TenantContext'
        };
    }
    /**
     * Validate tenant hash for security checks
     */
    validateTenantHash(hash) {
        return this._tenantHash === hash;
    }
    /**
     * Clean up cached access results
     */
    clearCache() {
        this._accessCache.clear();
    }
    /**
     * Get tenant context summary for debugging
     */
    getSummary() {
        return {
            tenant: this.tenant.id,
            user: this.user?.id || null,
            roles: this.user?.roles || [],
            permissions: this.user?.permissions || [],
            features: this.tenant.features,
            correlationId: this.request.correlationId
        };
    }
    /**
     * Serialize context for network transmission (excluding sensitive data)
     */
    toSerializable() {
        return {
            tenantId: this.tenant.id,
            userId: this.user?.id || null,
            correlationId: this.request.correlationId,
            requestId: this.request.requestId,
            timestamp: this.request.timestamp.toISOString(),
            roles: this.user?.roles || [],
            permissions: this.user?.permissions || [],
            features: this.tenant.features
        };
    }
}
/**
 * Factory for creating tenant contexts
 */
export class TenantContextFactory {
    /**
     * Create context from authentication token/session
     */
    static async fromAuth(tenantId, userId, authData) {
        // In production, this would validate the auth token and fetch tenant/user data
        const tenant = {
            id: tenantId,
            name: authData.tenantName || tenantId,
            type: authData.tenantType || 'enterprise',
            tier: authData.tenantTier || 'professional',
            features: authData.features || []
        };
        const user = {
            id: userId,
            email: authData.email,
            roles: authData.roles || ['user'],
            permissions: authData.permissions || [],
            actorId: authData.actorId,
            sessionId: authData.sessionId
        };
        return new TenantContext(tenant, user);
    }
    /**
     * Create system context (no user, system tenant)
     */
    static createSystemContext() {
        const tenant = {
            id: 'system',
            name: 'System',
            type: 'enterprise',
            tier: 'critical',
            features: ['all']
        };
        return new TenantContext(tenant, null);
    }
    /**
     * Create anonymous context for public operations
     */
    static createAnonymousContext(tenantId) {
        const tenant = {
            id: tenantId,
            name: 'Anonymous',
            type: 'enterprise',
            tier: 'basic',
            features: []
        };
        return new TenantContext(tenant, null);
    }
}
/**
 * Tenant context validation utilities
 */
export class TenantContextValidator {
    /**
     * Validate that operation is allowed for tenant/user combination
     */
    static async validateOperation(context, resource, operation, requireUser = true) {
        if (requireUser && !context.user) {
            return false;
        }
        return await context.validateAccess(resource, operation);
    }
    /**
     * Validate tenant isolation - ensure contexts don't cross tenant boundaries
     */
    static validateTenantIsolation(context1, context2) {
        return context1.getCurrentTenant() === context2.getCurrentTenant();
    }
    /**
     * Validate minimum role requirement
     */
    static validateMinimumRole(context, minimumRole) {
        if (!context.user)
            return false;
        const roleHierarchy = ['user', 'moderator', 'admin', 'super_admin'];
        const userMaxRole = context.user.roles.reduce((max, role) => {
            const roleIndex = roleHierarchy.indexOf(role);
            const maxIndex = roleHierarchy.indexOf(max);
            return roleIndex > maxIndex ? role : max;
        }, 'user');
        const minRoleIndex = roleHierarchy.indexOf(minimumRole);
        const userRoleIndex = roleHierarchy.indexOf(userMaxRole);
        return userRoleIndex >= minRoleIndex;
    }
}
//# sourceMappingURL=TenantContext.js.map